import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services } from '@ember/service';
import { getOwner } from '@ember/application';
import { isAfter } from 'date-fns/isAfter';
import { differenceInDays } from 'date-fns/differenceInDays';
import type { PropertyRowDataItem } from 'uplisting-frontend/services/calendar/data';
import BookingModel from 'uplisting-frontend/models/booking';
import UnavailabilityModel from 'uplisting-frontend/models/unavailability';
import { CALENDAR_DATES_PER_PAGE } from 'uplisting-frontend/services/calendar/manager';

export interface IOccasionMeta {
  isDraggable: boolean;
  isResizable: boolean;
  isClickable: boolean;
  cssClasses: string;
}

export class OccasionMeta implements IOccasionMeta {
  calendarManager!: Services['calendar/manager'];

  @cached @tracked _model!: PropertyRowDataItem;

  constructor(context, model: PropertyRowDataItem) {
    this.calendarManager = getOwner(context)?.lookup(
      'service:calendar/manager',
    ) as Services['calendar/manager'];

    this._model = model;
  }

  isClickable = true;
  isResizable = true;
  isDraggable = true;

  @cached
  get duration(): number {
    if (this._model instanceof UnavailabilityModel) {
      return this._model.duration;
    }

    return differenceInDays(this.endDate, this.startDate);
  }

  @cached
  get startDate(): Date {
    if (this._model instanceof BookingModel) {
      return this._model.startDate;
    } else if (this._model instanceof UnavailabilityModel) {
      return this._model.from;
    }

    return this._model.checkIn;
  }

  @cached
  get endDate(): Date {
    if (this._model instanceof BookingModel) {
      return this._model.endDate;
    } else if (this._model instanceof UnavailabilityModel) {
      return this._model.to;
    }

    return this._model.checkOut;
  }

  @cached
  get cssClasses(): string {
    const model = this._model;

    const classes = ['timeline-event'];

    if (model instanceof BookingModel) {
      classes.push(`booking-${model.status}`, `booking-${model.channel}`);

      if (model.problematic) {
        classes.push('booking-unpaid');
      }
    } else if (model instanceof UnavailabilityModel) {
      classes.push('unavailability-event');
    }

    let { duration } = this;

    const { startDate } = this.calendarManager.viewport;

    const isOngoing = isAfter(startDate, this.startDate);

    if (isOngoing) {
      duration -= differenceInDays(startDate, this.startDate);
    }

    duration =
      duration > CALENDAR_DATES_PER_PAGE ? CALENDAR_DATES_PER_PAGE : duration;

    classes.push(`duration-${duration}`);

    if (isOngoing) {
      classes.push('event-ongoing');
    }

    return classes.join(' ');
  }
}
